import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, DollarSign, Plus, ImagePlus, Ruler, PackagePlus, ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { createStockItem, updateStockItem, deleteStockItem, StockItem } from '../lib/stockItems';
import { uploadPurchaseOrderItemImage } from '../lib/purchaseOrders';
import SelectFieldQuoteMeasurementsModal, { MeasurementOrderItem } from './SelectFieldQuoteMeasurementsModal';
import { toast } from '../components/toasts/SimpleToast';
import { Customer } from '../lib/customers';

/** One created item handed back to the parent order flow. */
export interface CustomOrderItemResult {
  itemId: string;
  quantity: number;
  itemData: StockItem;
  comments?: string;
  images?: string[];
}

interface OneTimeItemOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: Customer;
  /** fieldQuoteId is set when any item came from field-quote measurements,
   * so the resulting PO can link to the customer journey.
   * removedItemIds lists previously created items the user deleted while editing. */
  onSuccess: (
    items: CustomOrderItemResult[],
    meta?: { fieldQuoteId?: string | null; removedItemIds?: string[] }
  ) => void;
  /** When set, the modal opens in edit mode pre-filled with these already-created
   * items (Back from the order summary). Submitting updates them instead of
   * creating duplicates. */
  initialItems?: CustomOrderItemResult[] | null;
}

interface ItemBlock {
  key: number;
  /** Set when this block edits an already-created stock item. */
  itemId?: string;
  name: string;
  cost: string;
  price: string;
  quantity: string;
  comments: string;
  images: string[];
}

// Grow the comments box with its content, capped at ~6 lines (then scroll).
const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
};

let blockKeyCounter = 0;
const newBlock = (): ItemBlock => ({
  key: ++blockKeyCounter,
  name: '',
  cost: '',
  price: '',
  quantity: '1',
  comments: '',
  images: [],
});

export default function OneTimeItemOrderModal({
  isOpen,
  onClose,
  supplier,
  onSuccess,
  initialItems
}: OneTimeItemOrderModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blocks, setBlocks] = useState<ItemBlock[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploadingKey, setUploadingKey] = useState<number | null>(null);
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
  // Field-quote businesses get a chooser on Add Item (new item vs from measurements).
  const [fieldQuotesEnabled, setFieldQuotesEnabled] = useState(false);
  const [showAddChooser, setShowAddChooser] = useState(false);
  const [showMeasurementsModal, setShowMeasurementsModal] = useState(false);
  const [sourceFieldQuoteId, setSourceFieldQuoteId] = useState<string | null>(null);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Edit mode: seed blocks from the already-created items so Back from the
      // order summary restores everything the user was working on.
      setBlocks(
        (initialItems || []).map((it) => ({
          ...newBlock(),
          itemId: it.itemId,
          name: it.itemData.name,
          cost: it.itemData.acquisition_cost > 0 ? String(it.itemData.acquisition_cost) : '',
          price:
            it.itemData.price > 0 && it.itemData.price !== it.itemData.acquisition_cost
              ? String(it.itemData.price)
              : '',
          quantity: String(it.quantity || 1),
          comments: it.comments || '',
          images: it.images || [],
        }))
      );
      setError(null);
      setShowAddChooser(false);
      setShowMeasurementsModal(false);
      setSourceFieldQuoteId(null);
    }
  }, [isOpen, initialItems]);

  // Same setting that gates field quotes elsewhere in the app (calendar FAB).
  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id || !supabase) return;
    supabase.rpc('get_app_setting', {
      p_business_id: selectedBusiness.id,
      p_app_slug: 'calendar',
      p_setting_key: 'show_field_quotes_in_fab',
      p_default_value: false,
    }).then(({ data, error: rpcError }) => {
      if (rpcError) {
        console.error('Error checking field quotes setting:', rpcError);
        setFieldQuotesEnabled(false);
      } else {
        setFieldQuotesEnabled(!!data);
      }
    });
  }, [isOpen, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const updateBlock = (key: number, patch: Partial<ItemBlock>) => {
    setBlocks(prev => prev.map(b => (b.key === key ? { ...b, ...patch } : b)));
  };

  const removeBlock = (key: number) => {
    setBlocks(prev => prev.filter(b => b.key !== key));
  };

  const addBlock = () => {
    setBlocks(prev => [...prev, newBlock()]);
  };

  const handleAddItemClick = () => {
    if (fieldQuotesEnabled) {
      setShowAddChooser(prev => !prev);
    } else {
      addBlock();
    }
  };

  // AI-generated items from field-quote measurements land as normal blocks
  // (cost/price stay blank — RFQ style) with the measurement photos attached.
  const handleMeasurementItems = (items: MeasurementOrderItem[], fieldQuoteId: string) => {
    setBlocks(prev => [
      ...prev,
      ...items.map((it) => ({
        ...newBlock(),
        name: it.name,
        quantity: String(it.quantity || 1),
        comments: it.comments,
        images: it.images,
      })),
    ]);
    setSourceFieldQuoteId(fieldQuoteId);
    setShowMeasurementsModal(false);
  };

  const handleImageFiles = async (key: number, files: FileList | null) => {
    if (!files || files.length === 0 || !selectedBusiness?.id) return;
    try {
      setUploadingKey(key);
      const urls: string[] = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        urls.push(await uploadPurchaseOrderItemImage(selectedBusiness.id, file));
      }
      if (urls.length > 0) {
        setBlocks(prev => prev.map(b => (b.key === key ? { ...b, images: [...b.images, ...urls] } : b)));
      }
    } catch (err) {
      console.error('Error uploading item image:', err);
      toast.error({ title: 'Upload failed', description: 'Could not upload image. Please try again.', duration: 4000 });
    } finally {
      setUploadingKey(null);
      const input = fileInputRefs.current[key];
      if (input) input.value = '';
    }
  };

  const removeImage = (key: number, url: string) => {
    setBlocks(prev => prev.map(b => (b.key === key ? { ...b, images: b.images.filter(u => u !== url) } : b)));
  };

  const blockTotal = (b: ItemBlock): string => {
    const cost = parseFloat(b.cost);
    const qty = parseInt(b.quantity);
    if (b.cost.trim() === '' || isNaN(cost) || cost <= 0) return 'TBD';
    if (isNaN(qty) || qty <= 0) return 'TBD';
    return `$${(cost * qty).toFixed(2)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedBusiness?.id) {
      setError('No business selected');
      return;
    }

    // Validate every block up front so nothing is half-created.
    for (const [i, b] of blocks.entries()) {
      const label = blocks.length > 1 ? `Item ${i + 1}: ` : '';
      if (!b.name.trim()) {
        setError(`${label}item name is required`);
        return;
      }
      // Cost is optional (request-for-quote style): blank means $0 and the
      // real price gets added to the PO after the supplier responds.
      const cost = b.cost.trim() === '' ? 0 : parseFloat(b.cost);
      if (isNaN(cost) || cost < 0) {
        setError(`${label}please enter a valid item cost`);
        return;
      }
      const price = b.price.trim() === '' ? cost : parseFloat(b.price);
      if (isNaN(price) || price < 0) {
        setError(`${label}please enter a valid retail price`);
        return;
      }
      const quantity = parseInt(b.quantity);
      if (isNaN(quantity) || quantity <= 0) {
        setError(`${label}please enter a valid quantity (greater than 0)`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const results: CustomOrderItemResult[] = [];
      for (const b of blocks) {
        const cost = b.cost.trim() === '' ? 0 : parseFloat(b.cost);
        const price = b.price.trim() === '' ? cost : parseFloat(b.price);
        const quantity = parseInt(b.quantity);

        // Blocks with an itemId edit an item that was already created on a
        // previous pass — update it instead of creating a duplicate.
        const stockItem = b.itemId
          ? await updateStockItem(b.itemId, {
              name: b.name.trim(),
              acquisition_cost: cost,
              price: price,
            })
          : await createStockItem({
              business_id: selectedBusiness.id,
              name: b.name.trim(),
              acquisition_cost: cost,
              price: price,
              description: null,
              category_id: null,
              supplier_id: supplier.id,
              unit: 'unit',
              sku: null,
              quantity: 0, // Start with 0 stock
              reorder_point: null, // One-time items don't have reorder points
              max_quantity: null, // One-time items don't have max quantity
              is_sellable: false,
              tax_rate: 0,
              is_one_time: true
            });

        results.push({
          itemId: stockItem.id,
          quantity,
          itemData: stockItem,
          comments: b.comments.trim() || undefined,
          images: b.images.length > 0 ? b.images : undefined,
        });
      }

      // Items that were created on a previous pass but removed while editing:
      // clean them up so orphan one-time items don't accumulate.
      const keptIds = new Set(blocks.map((b) => b.itemId).filter(Boolean));
      const removedItemIds = (initialItems || [])
        .map((it) => it.itemId)
        .filter((id) => !keptIds.has(id));
      for (const id of removedItemIds) {
        try {
          await deleteStockItem(id);
        } catch (err) {
          console.warn('Could not delete removed custom order item:', id, err);
        }
      }

      const isEdit = (initialItems?.length ?? 0) > 0;
      toast.success({
        title: isEdit
          ? 'Order Updated'
          : results.length === 1 ? 'Item Created' : 'Items Created',
        description: isEdit
          ? 'Your custom order changes have been saved.'
          : results.length === 1
            ? `"${results[0].itemData.name}" has been added to your order.`
            : `${results.length} items have been added to your order.`,
        duration: 3000,
      });

      onSuccess(results, { fieldQuoteId: sourceFieldQuoteId, removedItemIds });
      onClose();
    } catch (err) {
      console.error('Error creating custom order items:', err);
      setError(err instanceof Error ? err.message : 'Failed to create items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100070, // Higher than CreateOrderModal (100020)
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100080 // Higher than CreateOrderModal (100060)
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            {/* Back — returns to Create New Order (linear flow) */}
            <button
              type="button"
              onClick={onClose}
              className="absolute top-1/2 -translate-y-1/2 left-4 flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <ChevronLeft className="h-5 w-5" />
              Back
            </button>
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Create Custom Order
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Supplier: {`${supplier.first_name} ${supplier.last_name}`.trim()}
            </p>
          </div>

          {/* Content */}
          <form 
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-4`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              {/* Item blocks — styled like the order summary item cards */}
              {blocks.map((b, i) => (
                <div key={b.key} className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={b.name}
                      onChange={(e) => updateBlock(b.key, { name: e.target.value })}
                      placeholder={`Item name${blocks.length > 1 ? ` (${i + 1})` : ''}`}
                      className="flex-1 px-3 py-2 text-sm font-semibold bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeBlock(b.key)}
                      className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 active:scale-95 transition-all duration-150 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      aria-label="Remove item"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Item Cost</label>
                      <div className="relative">
                        <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={b.cost}
                          onChange={(e) => updateBlock(b.key, { cost: e.target.value })}
                          placeholder="TBD"
                          className="w-full pl-7 pr-2 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Retail Price</label>
                      <div className="relative">
                        <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={b.price}
                          onChange={(e) => updateBlock(b.key, { price: e.target.value })}
                          placeholder="Optional"
                          className="w-full pl-7 pr-2 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Qty</label>
                      <input
                        type="number"
                        min="1"
                        value={b.quantity}
                        onChange={(e) => updateBlock(b.key, { quantity: e.target.value })}
                        placeholder="1"
                        className="w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-600">
                    <span>
                      {b.cost.trim() === ''
                        ? 'Price TBD — awaiting quote from supplier'
                        : 'Cost you pay per unit'}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{blockTotal(b)}</span>
                  </div>

                  <textarea
                    ref={autoResizeTextarea}
                    value={b.comments}
                    onChange={(e) => {
                      updateBlock(b.key, { comments: e.target.value });
                      autoResizeTextarea(e.currentTarget);
                    }}
                    placeholder={'Item comments for the supplier (specs, measurements, etc.)'}
                    rows={1}
                    className="w-full px-3 py-2 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                    style={{ maxHeight: 140, overflowY: 'auto' }}
                  />

                  {/* Reference images */}
                  <div className="flex flex-wrap items-center gap-2">
                    {b.images.map((url) => (
                      <div key={url} className="relative group">
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={url}
                            alt="Reference"
                            className="h-14 w-14 object-cover rounded-lg border border-gray-200"
                          />
                        </a>
                        <button
                          type="button"
                          onClick={() => removeImage(b.key, url)}
                          className="absolute -top-1.5 -right-1.5 h-5 w-5 flex items-center justify-center bg-gray-800 text-white rounded-full shadow hover:bg-red-600 transition-colors"
                          aria-label="Remove image"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <input
                      ref={(el) => { fileInputRefs.current[b.key] = el; }}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleImageFiles(b.key, e.target.files)}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRefs.current[b.key]?.click()}
                      disabled={uploadingKey === b.key}
                      className="h-14 w-14 flex flex-col items-center justify-center gap-0.5 text-gray-400 hover:text-blue-600 bg-white border border-dashed border-gray-300 hover:border-blue-400 rounded-lg transition-colors disabled:opacity-50"
                      aria-label="Add reference images"
                    >
                      {uploadingKey === b.key ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      ) : (
                        <>
                          <ImagePlus className="h-4 w-4" />
                          <span className="text-[9px] leading-none">Photos</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}

              {blocks.length === 0 && !showAddChooser && (
                <div className="text-center py-6 text-sm text-gray-500">
                  No items yet — add your first item below.
                </div>
              )}

              {/* Add item: field-quote businesses choose between a blank item
                  and AI-generated items from field-quote measurements. */}
              {showAddChooser ? (
                <div className="space-y-2">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddChooser(false);
                      addBlock();
                    }}
                    className="flex flex-col items-center justify-center gap-2 px-4 py-5 text-sm font-semibold text-blue-600 bg-blue-50/50 border-2 border-dashed border-blue-200 rounded-xl hover:bg-blue-50 active:bg-blue-100 active:scale-[0.99] transition-all duration-150 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <PackagePlus className="h-5 w-5" />
                    New Item
                    <span className="text-[11px] font-normal text-gray-500">Enter details manually</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddChooser(false);
                      setShowMeasurementsModal(true);
                    }}
                    className="flex flex-col items-center justify-center gap-2 px-4 py-5 text-sm font-semibold text-purple-600 bg-purple-50/50 border-2 border-dashed border-purple-200 rounded-xl hover:bg-purple-50 active:bg-purple-100 active:scale-[0.99] transition-all duration-150 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Ruler className="h-5 w-5" />
                    From Measurements
                    <span className="text-[11px] font-normal text-gray-500">AI creates items from a field quote</span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddChooser(false)}
                  className="w-full py-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Cancel
                </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleAddItemClick}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-blue-600 bg-blue-50/50 border-2 border-dashed border-blue-200 rounded-xl hover:bg-blue-50 active:bg-blue-100 active:scale-[0.99] transition-all duration-150 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-4 w-4" />
                  Add Item
                </button>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> Items are created with 0 stock and added to your order — you'll receive
                  the inventory when it arrives. Leave the cost blank to request a quote; you can add pricing to
                  the PO after the supplier responds.
                </p>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 p-6 bg-gray-50 flex-shrink-0">
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || (blocks.length === 0 && !(initialItems?.length))}
              className="w-full p-3.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 shadow-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
               {loading ? (
                 <>
                   <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                   {initialItems?.length ? 'Saving...' : 'Creating...'}
                 </>
               ) : initialItems?.length ? (
                 'Update Order'
               ) : (
                 blocks.length > 1 ? `Add ${blocks.length} Items to Order` : 'Add to Order'
               )}
            </button>
          </div>
        </div>
      </div>

      {/* Field quote → measurements → AI item generation */}
      <SelectFieldQuoteMeasurementsModal
        isOpen={showMeasurementsModal}
        onClose={() => setShowMeasurementsModal(false)}
        supplierName={`${supplier.first_name} ${supplier.last_name}`.trim()}
        onItemsCreated={handleMeasurementItems}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}
